// src/Shop/ProductDetail.js
// صفحه جزئیات محصول - متصل به db.json

import React, { useContext, useState, useEffect, useRef } from "react";
import { CartContext } from "../context/CartContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { getProductById, getActiveProducts } from "../Services/productService";
import { getImageSrc } from "../Services/mediaService";
import "./Shop.css";

export default function ProductDetail({ navigateTo, productId }) {
  const { addProductToCart } = useContext(CartContext);

  const [product, setProduct] = useState(null);
  const [productsList, setProductsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const [isLensActive, setIsLensActive] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState("0% 0%");

  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    loadProductDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  const loadProductDetail = async () => {
    try {
      setLoading(true);
      setErrorMessage("");

      const selectedProduct = await getProductById(productId);
      const activeProducts = await getActiveProducts();

      setProduct(selectedProduct);
      setProductsList(Array.isArray(activeProducts) ? activeProducts : []);
    } catch (error) {
      console.error("خطا در دریافت جزئیات محصول:", error);
      setProduct(null);
      setErrorMessage(
        "محصول مورد نظر یافت نشد یا ارتباط با سرور برقرار نیست."
      );
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (value) => {
    return Number(value || 0).toLocaleString("fa-IR");
  };

  const handleAddToCart = () => {
    if (product && Number(product.count) > 0) {
      addProductToCart(product, productsList);
    }
  };

  const handleMouseMove = (e) => {
    if (!imageContainerRef.current || !imageRef.current) return;

    const { left, top, width, height } =
      imageContainerRef.current.getBoundingClientRect();

    const x = e.clientX - left;
    const y = e.clientY - top;

    const lensX = Math.min(Math.max(x, 0), width);
    const lensY = Math.min(Math.max(y, 0), height);

    setLensPosition({ x: lensX, y: lensY });

    const percentX = Math.min(Math.max((x / width) * 100, 0), 100);
    const percentY = Math.min(Math.max((y / height) * 100, 0), 100);

    setBackgroundPosition(`${percentX}% ${percentY}%`);
  };

  const handleMouseEnter = () => {
    setIsLensActive(true);
  };

  const handleMouseLeave = () => {
    setIsLensActive(false);
  };

  const getZoomBackgroundSize = () => {
    const containerWidth = imageContainerRef.current?.clientWidth || 400;
    const containerHeight = imageContainerRef.current?.clientHeight || 400;

    const naturalWidth = imageRef.current?.naturalWidth || 0;
    const naturalHeight = imageRef.current?.naturalHeight || 0;

    if (naturalWidth > containerWidth && naturalHeight > containerHeight) {
      return `${naturalWidth}px ${naturalHeight}px`;
    }

    return `${containerWidth * 2.4}px ${containerHeight * 2.4}px`;
  };

  if (loading) {
    return (
      <>
        <Header navigateTo={navigateTo} />

        <div className="container loading-container">
          <p>در حال دریافت اطلاعات محصول...</p>
        </div>

        <Footer navigateTo={navigateTo} />
      </>
    );
  }

  if (!product) {
    return (
      <>
        <Header navigateTo={navigateTo} />

        <div className="container error-container">
          <p>{errorMessage || "محصول یافت نشد!"}</p>

          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigateTo("shop")}
          >
            بازگشت به فروشگاه
          </button>
        </div>

        <Footer navigateTo={navigateTo} />
      </>
    );
  }

  return (
    <>
      <Header navigateTo={navigateTo} />

      <section className="container content-section">
        <button
          type="button"
          className="btn btn-secondary back-btn"
          onClick={() => navigateTo("shop")}
        >
          ← بازگشت به فروشگاه
        </button>

        <div className="product-detail-container">
          <div className="product-detail-image">
            <div
              ref={imageContainerRef}
              className="zoom-container"
              onMouseMove={handleMouseMove}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <img
                ref={imageRef}
                src={getImageSrc(product.img)}
                alt={product.title}
                className="zoom-image"
                onError={(e) => {
                  e.currentTarget.src = "/default-product.jpg";
                }}
              />

              {isLensActive && (
                <div
                  className="zoom-lens-overlay"
                  style={{
                    left: `${lensPosition.x}px`,
                    top: `${lensPosition.y}px`,
                    backgroundImage: `url(${getImageSrc(product.img)})`,
                    backgroundPosition: backgroundPosition,
                    backgroundSize: getZoomBackgroundSize(),
                  }}
                />
              )}
            </div>
          </div>

          <div className="product-detail-info">
            <h1 className="product-detail-title" style={{ textAlign: "right" }}>
              {product.title}
            </h1>

            <div className="product-detail-price" style={{ textAlign: "right" }}>
              <span className="price-label">قیمت :</span>
              <span className="price-value">
                {formatPrice(product.price)} تومان
              </span>
            </div>

            <div className="product-detail-stock" style={{ textAlign: "right" }}>
              <span className="stock-label">موجودی :</span>

              <div>
                <span
                  className={`stock-value ${
                    Number(product.count) > 0 ? "in-stock" : "out-of-stock"
                  }`}
                >
                  {Number(product.count) > 0
                    ? `موجود در انبار (${product.count} عدد)`
                    : "ناموجود"}
                </span>
              </div>
            </div>

            <div className="product-detail-description">
              <h3 style={{ textAlign: "right" }}>توضیحات :</h3>
              <p style={{ textAlign: "right" }}>{product.description}</p>
            </div>

            <button
              type="button"
              className="btn btn-primary add-to-cart-btn"
              onClick={handleAddToCart}
              disabled={Number(product.count) === 0}
            >
              🛒 افزودن به سبد خرید
            </button>
          </div>
        </div>
      </section>

      <Footer navigateTo={navigateTo} />
    </>
  );
}
